import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { FormElement, Label } from '@dashboard/components/Form';
import { isEmpty } from 'lodash';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { usePreview } from '@dashboard/context/PreviewContext';
import { Checkbox, Radio } from '@dashboard/components/Formik';
import { AfterPaymentActionEnum } from '@dashboard/enums/after-payment';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import usePage from '@/dashboard/hooks/usePage';

const DefaultConfirmationMessageForm = () => {
  const { page, updatePage } = usePage();

  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const checkoutValues = {
    showFilesList: page.showFilesList,
    showViewDownloadsButton: page.showViewDownloadsButton,
    afterPaymentAction: page.afterPaymentAction,
    customizeCheckoutConfirmation: page.customizeCheckoutConfirmation,
    showConfirmationEventName: page.showConfirmationEventName,
    showConfirmationEventDateTime: page.showConfirmationEventDateTime,
    showConfirmationEventLocation: page.showConfirmationEventLocation,
    showConfirmationEventMeetingLink: page.showConfirmationEventMeetingLink,
    showConfirmationEventTickets: page.showConfirmationEventTickets,
  };

  return (
    <Formik
      initialValues={checkoutValues}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const { data, error } = await updatePage.mutateAsync(values);

        if (data) {
          if (data.afterPaymentAction === AfterPaymentActionEnum.CONFIRMATION) {
            setPreview({
              type: 'confirmationCheckoutMessage',
            });
          }

          actions.setStatus('');
          actions.resetForm({
            values: {
              showFilesList: data.showFilesList,
              showViewDownloadsButton: data.showViewDownloadsButton,
              afterPaymentAction: data.afterPaymentAction,
              customizeCheckoutConfirmation: data.customizeCheckoutConfirmation,
              showConfirmationEventName: data.showConfirmationEventName,
              showConfirmationEventDateTime: data.showConfirmationEventDateTime,
              showConfirmationEventLocation: data.showConfirmationEventLocation,
              showConfirmationEventMeetingLink: data.showConfirmationEventMeetingLink,
              showConfirmationEventTickets: data.showConfirmationEventTickets,
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, dirty, setFieldValue, handleSubmit, isSubmitting }) => {
        useEffect(() => {
          setFieldValue('afterPaymentAction', AfterPaymentActionEnum.CONFIRMATION);
          setFieldValue('customizeCheckoutConfirmation', false);
        }, []);

        return (
          <Form onSubmit={handleSubmit}>
            {(page.type === PAGE_TYPES.CHECKOUT || page.type === PAGE_TYPES.FORM) && (
              <FormElement>
                <Label htmlFor="downloads">
                  Show on confirmation{' '}
                  <InfoTooltip>
                    If you have files attached to this checkout, you can choose to display them as a
                    list or have a button that links to the files.
                  </InfoTooltip>
                </Label>

                <Radio.Group name="downloads" id="downloads">
                  <Radio.Wrapper>
                    <Radio.Input
                      value="showFilesList"
                      id="showFilesList"
                      name="downloads"
                      checked={values.showFilesList}
                      onChange={e => {
                        setFieldValue('showFilesList', e.target.checked);
                        setFieldValue('showViewDownloadsButton', !e.target.checked);
                      }}
                    />
                    <Radio.Label htmlFor="showFilesList">List of files</Radio.Label>
                  </Radio.Wrapper>

                  <Radio.Wrapper>
                    <Radio.Input
                      value="showViewDownloadsButton"
                      id="showViewDownloadsButton"
                      name="downloads"
                      checked={values.showViewDownloadsButton}
                      onChange={e => {
                        setFieldValue('showViewDownloadsButton', e.target.checked);
                        setFieldValue('showFilesList', !e.target.checked);
                      }}
                    />
                    <Radio.Label htmlFor="showViewDownloadsButton">
                      "View downloads" button
                    </Radio.Label>
                  </Radio.Wrapper>
                </Radio.Group>
              </FormElement>
            )}

            {page.type === PAGE_TYPES.EVENT && (
              <>
                <Label>Show on confirmation</Label>
                <Checkbox.Wrapper>
                  <Checkbox id="showConfirmationEventName" name="showConfirmationEventName" />
                  <Checkbox.Label htmlFor="showConfirmationEventName">Event name</Checkbox.Label>
                </Checkbox.Wrapper>
                <Checkbox.Wrapper>
                  <Checkbox
                    id="showConfirmationEventDateTime"
                    name="showConfirmationEventDateTime"
                  />
                  <Checkbox.Label htmlFor="showConfirmationEventDateTime">
                    Date and time
                  </Checkbox.Label>
                </Checkbox.Wrapper>
                <Checkbox.Wrapper>
                  <Checkbox
                    id="showConfirmationEventLocation"
                    name="showConfirmationEventLocation"
                  />
                  <Checkbox.Label htmlFor="showConfirmationEventLocation">Location</Checkbox.Label>
                </Checkbox.Wrapper>

                <Checkbox.Wrapper>
                  <Checkbox
                    id="showConfirmationEventMeetingLink"
                    name="showConfirmationEventMeetingLink"
                  />
                  <Checkbox.Label htmlFor="showConfirmationEventMeetingLink">
                    Virtual event
                  </Checkbox.Label>
                </Checkbox.Wrapper>

                <Checkbox.Wrapper>
                  <Checkbox id="showConfirmationEventTickets" name="showConfirmationEventTickets" />
                  <Checkbox.Label htmlFor="showConfirmationEventTickets">
                    Tickets purchased
                  </Checkbox.Label>
                </Checkbox.Wrapper>
              </>
            )}

            <FormSubmit>
              <FormSubmit.Button>Save</FormSubmit.Button>
              <FormSubmit.State visible={!!dirty && !isSubmitting} />
            </FormSubmit>
          </Form>
        );
      }}
    </Formik>
  );
};

export default DefaultConfirmationMessageForm;
